import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FiCheck, FiCopy, FiKey, FiTrash2 } from "react-icons/fi";
import { AppButton, FormField, MessageBanner, SelectInput, TextInput } from "@/components/ui/primitives";
import { confirmAction } from "@/components/ui/confirmDialog";
import { useAuthStore } from "@/stores/useAuthStore";
import { useTranslation } from "react-i18next";
import {
  createMcpToken,
  mcpTokensQueryKey,
  revokeMcpToken,
  useMcpTokens,
  type CreateMcpTokenResponse,
  type McpTokenMetadata,
} from "../hooks/useMcpTokens";
import { useMcpPackage } from "@/domains/mcp/hooks/useMcpOAuth";
import { formatMcpDate, isMcpDateExpired } from "@/domains/mcp/utils/date";

export const McpTokensPanel = () => {
  const { t, i18n } = useTranslation(["profile", "common"]);
  const queryClient = useQueryClient();
  const hasSession = useAuthStore((state) => Boolean(state.token));
  const userId = useAuthStore((state) => state.user?.id);
  const tokensQuery = useMcpTokens(userId, hasSession);
  const packageQuery = useMcpPackage();
  const [label, setLabel] = useState("");
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [isSaving, setIsSaving] = useState(false);
  const [isRevokingId, setIsRevokingId] = useState<number | null>(null);
  const [createdToken, setCreatedToken] = useState<CreateMcpTokenResponse | null>(null);
  const [feedback, setFeedback] = useState<{ tone: "error" | "success"; message: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const refreshTokens = () => queryClient.invalidateQueries({ queryKey: mcpTokensQueryKey(userId) });

  const handleCreate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaving || !label.trim()) return;
    setIsSaving(true);
    setFeedback(null);
    try {
      const token = await createMcpToken(label.trim(), expiresInDays);
      setCreatedToken(token);
      setLabel("");
      setCopied(false);
      await refreshTokens();
    } catch {
      setFeedback({ tone: "error", message: t("profile.mcp.createError") });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setFeedback({ tone: "success", message: t("profile.mcp.copied") });
    } catch {
      setFeedback({ tone: "error", message: t("profile.mcp.copyError") });
    }
  };

  const handleRevoke = async (token: McpTokenMetadata) => {
    const confirmed = await confirmAction({
      title: t("profile.mcp.revokeTitle", { label: token.label }),
      body: t("profile.mcp.revokeBody"),
      confirmLabel: t("profile.mcp.revoke"),
      cancelLabel: t("common.actions.cancel"),
      tone: "danger",
    });
    if (!confirmed) return;

    setIsRevokingId(token.id);
    setFeedback(null);
    try {
      await revokeMcpToken(token.id);
      await refreshTokens();
    } catch {
      setFeedback({ tone: "error", message: t("profile.mcp.revokeError") });
    } finally {
      setIsRevokingId(null);
    }
  };

  const dismissSecret = () => {
    setCreatedToken(null);
    setCopied(false);
    setFeedback(null);
  };

  useEffect(() => {
    if (!createdToken) return;
    const metadata = tokensQuery.data?.find((token) => token.id === createdToken.id);
    if (metadata && (metadata.revoked_at || isMcpDateExpired(metadata.expires_at))) {
      setCreatedToken(null);
      setCopied(false);
      setFeedback(null);
    }
  }, [createdToken, tokensQuery.data]);

  const configSnippet = createdToken && packageQuery.data
    ? JSON.stringify({
        mcpServers: {
          "hey-jerry": {
            url: packageQuery.data.mcp_url,
            headers: { Authorization: `Bearer ${createdToken.token}` },
          },
        },
      }, null, 2)
    : null;

  return (
    <details className="app-panel-raised group mt-6 p-6 sm:p-7" open onToggle={(event) => {
      if (!event.currentTarget.open) dismissSecret();
    }}>
      <summary className="flex cursor-pointer list-none items-start justify-between gap-4 [&::-webkit-details-marker]:hidden">
        <div>
          <p className="app-domain-sidebar-label">{t("profile.mcp.eyebrow")}</p>
          <h2 className="text-lg font-semibold tracking-[-0.03em] text-[var(--app-text)] sm:text-xl">
            {t("profile.mcp.title")}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--app-text-secondary)]">
            {t("profile.mcp.description")}
          </p>
        </div>
        <FiKey aria-hidden="true" className="mt-1 h-5 w-5 shrink-0 text-[var(--app-text-secondary)]" />
      </summary>

      <div className="mt-6 space-y-6">
        <section aria-labelledby="mcp-oauth-heading" className="border-b border-[var(--app-border)] pb-6">
          <h3 id="mcp-oauth-heading" className="font-semibold text-[var(--app-text)]">
            {t("profile.mcp.oauthTitle")}
          </h3>
          <p className="mt-2 text-sm leading-6 text-[var(--app-text-secondary)]">
            {t("profile.mcp.oauthDescription")}
          </p>
          {packageQuery.data ? (
            <a
              href={packageQuery.data.download_url}
              className="mt-3 inline-flex min-h-11 items-center rounded-xl px-3 text-sm font-semibold text-[var(--app-primary)] underline underline-offset-4 hover:text-[var(--app-primary-hover)]"
            >
              {t("profile.mcp.downloadSkill", { version: packageQuery.data.version })}
            </a>
          ) : packageQuery.isLoading ? (
            <p className="mt-3 text-sm text-[var(--app-text-secondary)]">{t("profile.mcp.packageLoading")}</p>
          ) : (
            <p role="alert" className="mt-3 text-sm text-[var(--app-danger)]">{t("profile.mcp.packageError")}</p>
          )}
        </section>

        <details aria-labelledby="mcp-pat-heading" className="app-panel-inset p-4 sm:p-5" onToggle={(event) => {
          if (!event.currentTarget.open) dismissSecret();
        }}>
          <summary id="mcp-pat-heading" className="cursor-pointer font-semibold text-[var(--app-text)]">
            {t("profile.mcp.manualTitle")}
          </summary>
          <p className="mt-2 text-sm leading-6 text-[var(--app-text-secondary)]">
            {t("profile.mcp.manualDescription")}
          </p>

          {feedback ? (
            <MessageBanner role="status" tone={feedback.tone} className="mt-4">
              {feedback.message}
            </MessageBanner>
          ) : null}

          {createdToken ? (
            <div className="mt-5 space-y-4" aria-labelledby="mcp-token-created-title">
              <MessageBanner tone="success">
                <span id="mcp-token-created-title" className="font-semibold">{t("profile.mcp.secretOnceTitle")}</span>
                <span className="mt-1 block">{t("profile.mcp.secretOnceDescription")}</span>
              </MessageBanner>
              <FormField label={t("profile.mcp.secretLabel")} htmlFor="mcp-created-secret">
                <TextInput id="mcp-created-secret" type="text" readOnly value={createdToken.token} autoComplete="off" />
              </FormField>
              {packageQuery.data ? (
                <>
                  <FormField label={t("profile.mcp.serverUrl")} htmlFor="mcp-server-url">
                    <TextInput id="mcp-server-url" readOnly value={packageQuery.data.mcp_url} />
                  </FormField>
                  <pre className="overflow-x-auto rounded-xl border border-[var(--app-border)] bg-[var(--app-surface-soft)] p-4 text-xs leading-5 text-[var(--app-text)]">
                    <code>{configSnippet}</code>
                  </pre>
                  <AppButton type="button" tone="secondary" leadingIcon={copied ? <FiCheck /> : <FiCopy />} onClick={() => void handleCopy(configSnippet ?? "")}>
                    {copied ? t("profile.mcp.copied") : t("profile.mcp.copyConfig")}
                  </AppButton>
                </>
              ) : packageQuery.isLoading ? (
                <p className="text-sm text-[var(--app-text-secondary)]">{t("profile.mcp.packageLoading")}</p>
              ) : (
                <MessageBanner tone="default">{t("profile.mcp.packageError")}</MessageBanner>
              )}
              <div className="flex flex-wrap gap-3">
                <AppButton type="button" tone="secondary" leadingIcon={<FiCopy />} onClick={() => void handleCopy(createdToken.token)}>
                  {t("profile.mcp.copySecret")}
                </AppButton>
                <AppButton type="button" tone="primary" onClick={dismissSecret}>
                  {t("profile.mcp.savedSecret")}
                </AppButton>
              </div>
            </div>
          ) : (
            <form className="mt-5 grid gap-4 sm:grid-cols-2" onSubmit={(event) => void handleCreate(event)}>
              <FormField label={t("profile.mcp.label")} htmlFor="mcp-token-label" required>
                <TextInput
                  id="mcp-token-label"
                  value={label}
                  onChange={(event) => setLabel(event.target.value)}
                  placeholder={t("profile.mcp.labelPlaceholder")}
                  maxLength={100}
                  required
                  autoComplete="off"
                  disabled={isSaving}
                />
              </FormField>
              <FormField label={t("profile.mcp.expiry")} htmlFor="mcp-token-expiry">
                <SelectInput id="mcp-token-expiry" value={expiresInDays} onChange={(event) => setExpiresInDays(Number(event.target.value))} disabled={isSaving}>
                  {[30, 90, 180, 365].map((days) => (
                    <option key={days} value={days}>{t("profile.mcp.days", { count: days })}</option>
                  ))}
                </SelectInput>
              </FormField>
              <div className="sm:col-span-2">
                <AppButton type="submit" tone="secondary" isLoading={isSaving} leadingIcon={<FiKey />}>
                  {isSaving ? t("common.states.saving") : t("profile.mcp.create")}
                </AppButton>
              </div>
            </form>
          )}

          <div className="mt-6" aria-live="polite" aria-busy={tokensQuery.isLoading}>
            {tokensQuery.isLoading ? (
              <p className="text-sm text-[var(--app-text-secondary)]">{t("profile.mcp.loading")}</p>
            ) : tokensQuery.isError ? (
              <div>
                <p role="alert" className="text-sm text-[var(--app-danger)]">{t("profile.mcp.loadError")}</p>
                <AppButton type="button" tone="ghost" size="sm" className="mt-2" onClick={() => void tokensQuery.refetch()}>
                  {t("common.actions.retry", { defaultValue: "Retry" })}
                </AppButton>
              </div>
            ) : tokensQuery.data?.length ? (
              <ul className="divide-y divide-[var(--app-border)]">
                {tokensQuery.data.map((token) => {
                  const expired = isMcpDateExpired(token.expires_at);
                  const revoked = Boolean(token.revoked_at);
                  const active = !expired && !revoked;
                  const lastUsed = formatMcpDate(token.last_used_at, i18n.language);
                  const expiry = formatMcpDate(token.expires_at, i18n.language);
                  return (
                    <li key={token.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-[var(--app-text)]">{token.label}</p>
                        <p className="mt-1 text-xs leading-5 text-[var(--app-text-secondary)]">
                          {t("profile.mcp.status")}: {t(active ? "profile.mcp.active" : revoked ? "profile.mcp.revoked" : "profile.mcp.expired")}
                          {expiry ? ` · ${t("profile.mcp.expires", { date: expiry })}` : ""}
                        </p>
                        <p className="text-xs leading-5 text-[var(--app-text-secondary)]">
                          {t("profile.mcp.lastAuthentication", { date: lastUsed ?? t("profile.mcp.never") })}
                        </p>
                      </div>
                      {active ? (
                        <AppButton
                          type="button"
                          tone="dangerSoft"
                          size="sm"
                          isLoading={isRevokingId === token.id}
                          disabled={isRevokingId !== null}
                          leadingIcon={<FiTrash2 />}
                          onClick={() => void handleRevoke(token)}
                        >
                          {t("profile.mcp.revoke")}
                        </AppButton>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-[var(--app-text-secondary)]">{t("profile.mcp.empty")}</p>
            )}
          </div>
        </details>
      </div>
    </details>
  );
};
